import _ from 'lodash';
import {
  DROP_TABLE,
  FETCH_OBJECTS_BEGIN,
  FETCH_OBJECTS_FAILURE,
  FETCH_OBJECTS_SUCCESS,
  FETCH_OBJECT_FAILURE,
  SELECT_ALL_OBJECTS_BEGIN,
  SELECT_ALL_OBJECTS_FAILURE,
  SELECT_ALL_OBJECTS_SUCCESS,
  SELECT_OBJECTS,
  SET_CURRENT_TABLE_ID,
  SET_ORDER,
  UNSELECT_OBJECTS,
  UPDATE_SELECTED_COLUMNS
} from '../actions/tableOnSteroids';

const fetchObjectsSuccess = (state, previousState, { opts, data }) => {
  // All done: set loading "false".
  // Also, replace the items with the ones from the server
  const { tableId, updatedObjects, appendOldState } = opts;
  const url = opts.url || previousState.url;
  const page = data.page || opts.page || previousState.page;
  const query = opts.query || previousState.query;
  let newSelectedObjects = previousState.selectedObjects;

  let newObjects = data.objects.reduce((acc, row) => ({ ...acc, [row.key || row.id]: row }), {});
  let ids = data.objects.map(row => row.key || row.id.toString());

  // When updating objects in the list we want the table to keep the objects that were just changed and
  // load the new objects if necessary

  if (appendOldState) {
    newObjects = {};
    let wantedObjects = { ...previousState.objects, ...newObjects };

    Object.keys(wantedObjects).forEach((objectKey) => {
      if (Object.keys(updatedObjects).includes(objectKey)) {
        if (!updatedObjects[objectKey].deleted_at)
          newObjects[objectKey] = updatedObjects[objectKey];
      } else {
        newObjects[objectKey] = wantedObjects[objectKey];
      }
    });

    ids = _([...previousState.ids, ...ids]).filter(id => newObjects[id]).uniq().value();

    const previousSelected = previousState.selectedObjects;
    newSelectedObjects = {};

    Object.keys(previousSelected).forEach((objectKey) => {
      if (Object.keys(updatedObjects).includes(objectKey)) {
        if (!updatedObjects[objectKey].deleted_at) {
          newSelectedObjects[objectKey] = updatedObjects[objectKey];
        }
      } else {
        newSelectedObjects[objectKey] = previousSelected[objectKey];
      }
    });
  }

  let newState = {
    objects: newObjects,
    lastPage: data.last_page,
    totalCount: data.total_count,
    selectedObjects: newSelectedObjects,
    error: false,
    page,
    query,
    url,
    ids,
    loading: false,
    downloadCsv: data.download_csv,
    asyncCsv: data.async_csv,
    csvDownloadable: data.csv_downloadable,
  };

  if (!previousState.columns.length) {
    newState.columns = data.columns;
  }

  if (opts.objectType) {
    newState.objectType = opts.objectType;
  }

  return {
    ...state,

    [tableId]: {
      ...previousState,
      ...newState,
      error: null
    }
  };
};

export default function tableOnSteroids(state = {}, action) {
  let previousState;
  const defaultTableProps = {
    columns: {},
    lastPage: 10000,
    objects: {},
    order: { column: null, ascending: true },
    page: 1,
    query: {},
    selectedObjects: {}
  };

  if (action.opts) {
    previousState = state[action.opts.tableId] || defaultTableProps;
  }

  let opts = action.opts;
  let objectsState = {};
  let newObjects;

  switch (action.type) {
    case FETCH_OBJECTS_BEGIN:
      // Mark the state as "loading" so we can show a spinner or something
      // Also, reset any errors. We're starting fresh.
      newObjects = previousState.objects;
      if (opts.updatedObjects) {
        Object.keys(newObjects).forEach((objectKey) => {
          if (Object.keys(opts.updatedObjects).includes(objectKey)) {
            newObjects[objectKey] = opts.updatedObjects[objectKey];
          }
        });
      }
      objectsState[action.opts.tableId] = {
        ...previousState,
        objects: { ...newObjects },
        loading: action.opts && action.opts.loading && true,
        error: null
      };
      return {
        ...state,
        ...objectsState
      };

    case FETCH_OBJECTS_SUCCESS:
      return fetchObjectsSuccess(state, previousState, action);

    case FETCH_OBJECTS_FAILURE:
      // The request failed, but it did stop, so set loading to "false".
      // Save the error, and we can display it somewhere
      // Since it failed, we don't have items to display anymore, so set it empty.
      // This is up to you and your app though: maybe you want to keep the items
      // around! Do whatever seems right.
      objectsState[action.opts.tableId] = {
        ...previousState,
        loading: false,
        error: action.error
      };
      return {
        ...state,
        ...objectsState
      };
    case FETCH_OBJECT_FAILURE:
      newObjects = previousState.objects;
      newObjects[action.opts.id] = { ...previousState.objects[action.opts.id], error: action.error, loading: false };
      objectsState[action.opts.tableId] = {
        ...previousState,
        objects: { ...newObjects },
        loading: false,
        error: null
      };
      return {
        ...state,
        ...objectsState
      };
    case UPDATE_SELECTED_COLUMNS:
      objectsState[action.opts.tableId] = {
        ...previousState,
        selectedColumns: action.selectedColumns
      };
      return {
        ...state,
        ...objectsState
      };
    case SELECT_OBJECTS:
      objectsState = {};
      objectsState[action.opts.tableId] = {
        ...previousState,
        selectedObjects: { ...previousState.selectedObjects, ...action.objects },
        selectedObjectError: null,
        selectedObjectLoader: false
      };
      return {
        ...state,
        ...objectsState
      };
    case UNSELECT_OBJECTS:
      let previousSelectedObjects = previousState.selectedObjects;
      let unselectedObjects = action.objects;
      let selectedObjects;
      if (action.all) {
        selectedObjects = {};
      } else {
        Object.keys(unselectedObjects).forEach((key) => {
          if (previousSelectedObjects.hasOwnProperty(key)) {
            delete previousSelectedObjects[key];
            selectedObjects = { ...previousSelectedObjects };
          }
        });
      }
      objectsState[action.opts.tableId] = {
        ...previousState,
        selectedObjects,
        selectedObjectError: null,
        selectedObjectLoader: false
      };
      return {
        ...state,
        ...objectsState
      };
    case SET_CURRENT_TABLE_ID:
      return {
        ...state,
        tableId: action.tableId
      };
    case SELECT_ALL_OBJECTS_BEGIN:
      objectsState = {};
      objectsState[action.opts.tableId] = {
        ...previousState,
        selectedObjectError: null,
        selectedObjectLoader: true
      };
      return {
        ...state,
        ...objectsState
      };
    case SELECT_ALL_OBJECTS_SUCCESS:
      newObjects = action.data.objects;
      objectsState = {};
      objectsState[action.opts.tableId] = {
        ...previousState,
        selectedObjectLoader: false,
        selectedObjectError: null,
        selectedObjects: { ...previousState.selectedObjects, ...newObjects }
      };
      return {
        ...state,
        ...objectsState
      };
    case SELECT_ALL_OBJECTS_FAILURE:
      objectsState = {};
      objectsState[action.opts.tableId] = {
        ...previousState,
        selectedObjectLoader: false,
        selectedObjectError: action.error
      };
      return {
        ...state,
        ...objectsState
      };
    case SET_ORDER:
      return {
        ...state,
        [action.opts.tableId]: {
          ...previousState,
          order: _.pick(action, ['columnKey', 'ascending'])
        }
      };
    case DROP_TABLE:
      return {
        ...state,
        [action.opts.tableId]: {
          ...defaultTableProps,
        }
      };
    default:
      // ALWAYS have a default case in a reducer
      return state;
  }
}
